import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ILegalSettings, ILegalSettingsForm, LegalOverridesPatch } from '@/lib/legal/legal.model';

const LEGAL_SETTINGS_KEY = 'legal-settings';

/**
 * Impostazioni legali dell'utente corrente, gia' risolte lato server: per un membro
 * del team la RPC ritorna l'anagrafica dell'agenzia (`inherited: true`, `can_edit: false`)
 * e, per ogni documento, se lo eredita, se l'ha personalizzato e se e' rimasto indietro.
 */
export const useLegalSettings = () => {
  const { userData } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<ILegalSettingsForm>({
    queryKey: [LEGAL_SETTINGS_KEY, userData?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_legal_settings' as any);
      if (error) throw error;
      return (data ?? {}) as ILegalSettingsForm;
    },
    enabled: !!userData?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: ILegalSettings) => {
      const { data, error } = await supabase.rpc('save_my_legal_settings' as any, {
        p_legal_name: values.legal_name ?? '',
        p_legal_email: values.legal_email ?? '',
        p_legal_address: values.legal_address ?? '',
        p_tax_id: values.tax_id ?? '',
        p_cancellation_policy: values.cancellation_policy ?? '',
        p_privacy_policy: values.privacy_policy ?? '',
        p_terms_conditions: values.terms_conditions ?? '',
      });
      if (error) throw error;
      return data as ILegalSettingsForm;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([LEGAL_SETTINGS_KEY, userData?.id], data);
    },
  });

  // Membro del team: personalizza o ripristina i singoli documenti.
  const saveOverridesMutation = useMutation({
    mutationFn: async (documents: LegalOverridesPatch) => {
      const { data, error } = await supabase.rpc('save_my_legal_overrides' as any, {
        p_documents: documents,
      });
      if (error) throw error;
      return data as ILegalSettingsForm;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([LEGAL_SETTINGS_KEY, userData?.id], data);
    },
  });

  // Agenzia: notifica in-app a tutti i membri perche' rivedano i propri testi.
  const requestMemberUpdateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('request_legal_update_from_members' as any);
      if (error) throw error;
      return (data ?? 0) as number;
    },
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending || saveOverridesMutation.isPending,
    saveOverrides: saveOverridesMutation.mutateAsync,
    requestMemberUpdate: requestMemberUpdateMutation.mutateAsync,
    isRequestingMemberUpdate: requestMemberUpdateMutation.isPending,
  };
};
